import * as $ from 'jquery';
import { disposableSubscription } from 'common/barrels/core';
import { Subject, Observable } from 'common/barrels/rx';

import { Control } from 'runtime/controls/control';
import { ControlArgs } from 'runtime/controls/controlargs';
import { registerBinding } from 'runtime/bindingengine';
import { Color } from 'common/color';
import * as rxui from 'common/rxui/core';

import 'runtime/styles/controls/textbox.less';

const defaultPopulationDelay = 250;

export class TextBox extends Control {
    private $input: JQuery;
    private acceptsReturn: boolean;
    private wrap: boolean;
    private manualUpdates: Subject<boolean>;

    constructor(args: ControlArgs) {
        super(args);

        this.methods.register(this.config.elementName, this);
        this.$element.addClass('control-container textbox');
        this.acceptsReturn = this.config['content.AcceptsReturn'];
        this.wrap = this.config['content.WrapText'];

        this.$input = (this.acceptsReturn || this.wrap
                ? $('<textarea />')
                : $('<input type="text" />'))
            .conditionalAttr(!this.wrap, 'wrap', 'off')
            .val(this.config['content.Text'])
            .css('text-align', this.config['content.TextAlignment'].toLowerCase())
            .css('font-size', this.config['content.FontSize'] + 'px')
            .css('font-weight', this.config['content.FontWeight'].toLowerCase())
            .conditionalCss(this.config['content.FontFamily'], 'font-family')
            .conditionalCss(this.config['content.FontStyle'], 'font-style')
            .conditionalCss(this.config['content.Foreground'], 'color', () => Color.splitArgb(this.config['content.Foreground'])[0])
            .applyBackground(this.config['content.Background'])
            .conditionalAttr(this.config['content.MaxLength'], 'maxlength')
            .toggleClass('textbox-noscroll', this.acceptsReturn && !this.wrap)
            .appendTo(this.$element);

        const spellcheck = this.config['spellcheck'];
        if (spellcheck === 'true')
            this.$input.attr('spellcheck', 'true');

        if (spellcheck === 'false')
            this.$input.attr('spellcheck', 'false');

        this.applyTooltip(this.$input);
        this.manualUpdates = new Subject<boolean>();

        var immediateUpdates = rxui.from(this.$input, 'input propertychange').map(() => true);
        var delayedUpdates = rxui.from(this.$input, 'focusin')
                                    .map(() => this.$input.val())
                                    .flatMap(x => rxui.from(this.$input, 'focusout').take(1).map(() => x))
                                    .filter(x => x !== this.$input.val()).map(() => true);

        const debouncedUpdates = rxui.from(this.$input, 'focusin').flatMap(() => immediateUpdates.takeUntil(rxui.from(this.$input, 'focusout')).debounceTime(this.config['debounceDelay'] || defaultPopulationDelay));

        var updateTrigger = this.config['content.UpdateBindingOnTextChanged'] ? immediateUpdates : delayedUpdates;
        var updates = this.manualUpdates.merge(updateTrigger).map(x => ({ trigger: x, value: this.$input.val() })).distinctUntilChanged(null, x => x.value).filter(x => x.trigger === true).map(x => x.value);
        var displayedValues = Observable.of<string>(null).merge(this.manualUpdates.merge(immediateUpdates).map(() => this.$input.val())).distinctUntilChanged().pairwise().share();
        var enterPressed = rxui.filteredKeys(this.$input, [$.ui.keyCode.ENTER]).share();
        var nullOrEmpty = x => x === null || x === '';

        this.subscriptions = disposableSubscription(
            displayedValues.filter(x => nullOrEmpty(x[0]) && !nullOrEmpty(x[1])).subscribe(() => this.triggerEvent('ValueEntered')),
            displayedValues.filter(x => !nullOrEmpty(x[0]) && nullOrEmpty(x[1])).subscribe(() => this.triggerEvent('ValueCleared')),
            updates.subscribe(x => this.$element.triggerHandler('content.Text', x)),
            delayedUpdates.subscribe(() => this.triggerEvent('Lost Focus and Value Changed')),
            this.processBindings(this.config),
            enterPressed.subscribe(() => this.triggerEvent('EnterPressed')),
            enterPressed.filter(() => this.wrap && !this.acceptsReturn).subscribe(e => e.preventDefault()),
            debouncedUpdates.subscribe(() => this.triggerEvent('DebouncedValueChanged')),
            this.manualUpdates,
            this.managePrimaryBinding('content.Text')
            //rxui.applyIE8SizeAndCenteringHackIfNecessary(this.$element, this.$input)
        );

        this.initialize();
    }

    'content.Text'(text: string) {
        this.checkInitialValueReceived();

        if (!this.valuesEquivalent(text, this.$input.val())) {
            this.$input.val(text);
            this.manualUpdates.next(false);
        }            
    }

    'set Text'(text: string) {
        this.$input.val(text);
        this.manualUpdates.next(true);
    }

    private valuesEquivalent(a: string, b: string) {
        return a === b ? true
                : a === null && b === '' ? true
                : a === '' && b === null ? true
                : false;
    }

    applyEnabledState(value: boolean) {
        this.$input.prop('readonly', !value);

        // any time you find yourself thinking "why in the world would anyone do this?!?" there's a really good chance the answer is "IE".
        if (this.$input.is(':focus'))
            this.focus();
    }

    focus() {
        // why blur? IE.
        this.$input.blur().focus();
    }

    tabIndexElements() {
        return this.$input;
    }
}

registerBinding('textbox', TextBox);